/**
 * Commodity Modify Dialog (KAN-307)
 *
 * Side-by-side current vs. new value form for editing commodity trades.
 * Handles: field editing, live quantity recalculation, maturity month
 * warning, comment, and confirmation summary.
 *
 * Field order and types match the user's specification:
 *   - Read-only context: Product, Underlying, Our Entity, LegacyReference1
 *   - Editable: Counterparty (dropdown), Direction, TradeDate, ValueDate,
 *     Lots, Price, Quantity (live-calculated), OptionType (if Option),
 *     MaturityDate, FixingReference (dropdown), PaymentDate, Grouping1/2/3 (text)
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import { commodityModificationService } from '../../../services/api/commodityModificationService';
import { clientService } from '../../../services/api/clientService';

// Industry-standard futures month codes
const MONTH_CODES: { letter: string; name: string; month: number }[] = [
  { letter: 'F', name: 'Jan', month: 1 },
  { letter: 'G', name: 'Feb', month: 2 },
  { letter: 'H', name: 'Mar', month: 3 },
  { letter: 'J', name: 'Apr', month: 4 },
  { letter: 'K', name: 'May', month: 5 },
  { letter: 'M', name: 'Jun', month: 6 },
  { letter: 'N', name: 'Jul', month: 7 },
  { letter: 'Q', name: 'Aug', month: 8 },
  { letter: 'U', name: 'Sep', month: 9 },
  { letter: 'V', name: 'Oct', month: 10 },
  { letter: 'X', name: 'Nov', month: 11 },
  { letter: 'Z', name: 'Dec', month: 12 },
];

/**
 * Parse a fixing reference like 'WQ26' → { prefix: 'W', monthLetter: 'Q', year2d: '26' }
 */
function parseFixingRef(ref: string): { prefix: string; monthLetter: string; year2d: string } | null {
  if (!ref || ref.length < 3) return null;
  const year2d = ref.slice(-2);
  const monthLetter = ref.charAt(ref.length - 3);
  const prefix = ref.slice(0, -3);
  if (!/^\d{2}$/.test(year2d)) return null;
  if (!MONTH_CODES.find(m => m.letter === monthLetter)) return null;
  return { prefix, monthLetter, year2d };
}

/**
 * Generate fixing reference options for a given prefix.
 * Produces codes for a ±2-year window (24 months before, 24 months ahead).
 */
function generateFixingRefOptions(currentRef: string): string[] {
  const parsed = parseFixingRef(currentRef);
  if (!parsed) return [currentRef];
  const { prefix } = parsed;
  const now = new Date();
  const options: string[] = [];
  for (let offset = -24; offset <= 24; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const y2d = String(d.getFullYear()).slice(-2);
    const mc = MONTH_CODES[d.getMonth()];
    options.push(`${prefix}${mc.letter}${y2d}`);
  }
  return options;
}

// ── Field configuration ──

type FieldType = 'readonly' | 'display' | 'text' | 'number' | 'date' | 'select' | 'fixingRef' | 'counterparty';

interface FieldConfig {
  key: string;
  type: FieldType;
  options?: string[];
  optionOnly?: boolean;
  optional?: boolean;
}

const DIALOG_FIELDS: FieldConfig[] = [
  // Read-only context (display only, not editable, not sent to API)
  { key: 'ProductType', type: 'display' },
  { key: 'UnderlyingName', type: 'display' },
  { key: 'OurParty', type: 'display' },
  { key: 'LegacyReference1', type: 'display' },
  // Editable fields in specified order
  { key: 'CounterpartyName', type: 'counterparty' },
  { key: 'Direction', type: 'select', options: ['Buy', 'Sell'] },
  { key: 'TradeDate', type: 'date' },
  { key: 'ValueDate', type: 'date' },
  { key: 'Lots', type: 'number' },
  { key: 'Price', type: 'number' },
  { key: 'Quantity', type: 'readonly' },
  { key: 'OptionType', type: 'select', options: ['Put', 'Call'], optionOnly: true },
  { key: 'MaturityDate', type: 'date' },
  { key: 'FixingReference', type: 'fixingRef' },
  { key: 'PaymentDate', type: 'date' },
  { key: 'Grouping1', type: 'text', optional: true },
  { key: 'Grouping2', type: 'text', optional: true },
  { key: 'Grouping3', type: 'text', optional: true },
];

interface Props {
  trade: any;
  clientId: string;
  canSelfApprove: boolean;
  onSuccess: (result: any) => void;
  onClose: () => void;
}

/**
 * Parse a locale-formatted number string, accepting both '.' and ',' as
 * decimal separators regardless of the current locale.
 */
function parseLocaleNumber(input: string): number {
  if (!input || !input.trim()) return NaN;
  let s = input.trim();
  // Determine which separator is the decimal: the LAST occurrence of '.' or ','
  const lastDot = s.lastIndexOf('.');
  const lastComma = s.lastIndexOf(',');
  if (lastComma > lastDot) {
    // Comma is the decimal separator (e.g. "1.234,56" or "1234,56")
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (lastDot > lastComma) {
    // Dot is the decimal separator (e.g. "1,234.56" or "1234.56")
    s = s.replace(/,/g, '');
  } else {
    // No separator or equal position — just try parsing
    s = s.replace(/,/g, '');
  }
  return Number(s);
}

const CommodityModifyDialog: React.FC<Props> = ({ trade, clientId, canSelfApprove, onSuccess, onClose }) => {
  const { t, i18n } = useTranslation();

  // Editable fields only (not display/readonly)
  const editableFields = useMemo(
    () => DIALOG_FIELDS.filter(f => f.type !== 'display' && f.type !== 'readonly'),
    []
  );

  // Form values — start with current trade values
  const [formValues, setFormValues] = useState<Record<string, any>>(() => {
    const initial: Record<string, any> = {};
    for (const f of editableFields) {
      initial[f.key] = trade[f.key] ?? '';
    }
    return initial;
  });

  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set());
  const commentRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);

  // Commodity counterparty names (loaded from API)
  const [counterpartyOptions, setCounterpartyOptions] = useState<{ counterpartyName: string; bankId: string }[]>([]);

  const entityId = trade.entityId || clientId;
  const isOption = trade.ProductType === 'Option';

  // Load commodity counterparty names
  useEffect(() => {
    clientService.getCounterpartyNames(clientId, 'commodity').then(res => {
      if (res.success && res.data) setCounterpartyOptions(res.data);
    }).catch(() => { /* fall back to text input */ });
  }, [clientId]);

  // Quantity calculation uses real underlying factors from the API when available.
  // Formula: Price × quantityConversionFactor × Lots × lotSizeMultiplier × quantityMultiplier
  // Fallback: derive combined factor from the trade's own values (Quantity / (Price × Lots)).
  const hasUnderlyingFactors = trade._quantityMode !== undefined;
  const quantityMode = trade._quantityMode || 'formula';
  const quantityConversionFactor = Number(trade._quantityConversionFactor) || 1;
  const lotSizeMultiplier = Number(trade._lotSizeMultiplier) || 1;
  const quantityMultiplier = Number(trade._quantityMultiplier) || 1;

  // Fallback combined factor derived from existing trade values
  const derivedFactor = useMemo(() => {
    const origPrice = Number(trade.Price);
    const origLots = Number(trade.Lots);
    const origQty = Number(trade.Quantity);
    if (!origPrice || !origLots || !origQty) return null;
    return origQty / (origPrice * origLots);
  }, [trade.Price, trade.Lots, trade.Quantity]);

  // Generate fixing reference dropdown options from the trade's current reference
  const fixingRefOptions = useMemo(
    () => generateFixingRefOptions(trade.FixingReference || ''),
    [trade.FixingReference]
  );

  // Live quantity preview — recalculated immediately when Price or Lots change
  const previewQuantity = useMemo(() => {
    if (quantityMode === 'direct') return null;
    const curPrice = parseLocaleNumber(String(formValues.Price ?? trade.Price));
    const curLots = parseLocaleNumber(String(formValues.Lots ?? trade.Lots));
    if (isNaN(curPrice) || isNaN(curLots) || !curPrice || !curLots) return null;
    if (hasUnderlyingFactors) {
      return curPrice * quantityConversionFactor * curLots * lotSizeMultiplier * quantityMultiplier;
    }
    // Fallback: use derived factor from original trade values
    if (derivedFactor == null) return null;
    return curPrice * curLots * derivedFactor;
  }, [quantityMode, hasUnderlyingFactors, quantityConversionFactor, lotSizeMultiplier, quantityMultiplier, derivedFactor, formValues.Price, formValues.Lots, trade.Price, trade.Lots]);

  // Derive which fields actually changed
  const changedFields = useMemo(() => {
    const changed: Record<string, any> = {};
    for (const f of editableFields) {
      const original = trade[f.key] ?? '';
      const current = formValues[f.key] ?? '';
      if (f.type === 'number') {
        const origNum = original === '' ? '' : Number(original);
        const curNum = current === '' ? '' : parseLocaleNumber(String(current));
        if (origNum !== curNum && !isNaN(curNum as number)) changed[f.key] = curNum;
      } else {
        if (String(original) !== String(current)) changed[f.key] = current;
      }
    }
    return changed;
  }, [formValues, trade, editableFields]);

  const hasChanges = Object.keys(changedFields).length > 0;

  // Show fixing reference hint only when maturity month has actually changed
  // (i.e. MaturityDate is in changedFields AND the month differs)
  const maturityMonthChanged = useMemo(() => {
    if (!('MaturityDate' in changedFields)) return false;
    const origDate = trade.MaturityDate || '';
    const newDate = String(changedFields.MaturityDate || '');
    if (!origDate || !newDate) return false;
    return origDate.substring(0, 7) !== newDate.substring(0, 7);
  }, [changedFields, trade.MaturityDate]);

  const priceOrLotsChanged = 'Price' in changedFields || 'Lots' in changedFields;

  // Field label lookup
  const fieldLabel = useCallback((key: string) => {
    return t(`commodities.fields.${key.charAt(0).toLowerCase() + key.slice(1)}`, key);
  }, [t]);

  // Format date as DD/MM/YYYY
  const formatDate = useCallback((value: any): string => {
    if (!value) return '—';
    const s = String(value).substring(0, 10);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : s;
  }, []);

  // Format number with locale-specific separators
  const formatNumber = useCallback((value: any): string => {
    if (value == null || value === '') return '—';
    const num = Number(value);
    if (isNaN(num)) return String(value);
    return num.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }, [i18n.language]);

  // Format current value for display
  const formatValue = useCallback((key: string, value: any) => {
    if (value == null || value === '') return '—';
    // Check if this field is a date type
    const fieldDef = DIALOG_FIELDS.find(f => f.key === key);
    if (fieldDef?.type === 'date') return formatDate(value);
    if (fieldDef?.type === 'number' || fieldDef?.type === 'readonly') return formatNumber(value);
    return String(value);
  }, [formatDate, formatNumber]);

  const handleFieldChange = useCallback((key: string, value: string) => {
    setFormValues(prev => ({ ...prev, [key]: value }));
    setFieldErrors(prev => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, []);

  const handleSubmit = useCallback(async () => {
    if (canSelfApprove) {
      // Single-step: validate field changes
      if (!hasChanges) return;

      const errors = new Set<string>();
      for (const f of editableFields) {
        if (f.optional) continue;
        if (f.optionOnly && !isOption) continue;
        const val = formValues[f.key] ?? '';
        if (String(val).trim() === '') {
          errors.add(f.key);
        } else if (f.type === 'number') {
          const num = parseLocaleNumber(String(val));
          if (isNaN(num) || num <= 0) errors.add(f.key);
        }
      }
      if (errors.size > 0) {
        setFieldErrors(errors);
        const firstKey = editableFields.find(f => errors.has(f.key))?.key;
        if (firstKey && tableRef.current) {
          const row = tableRef.current.querySelector(`[data-field="${firstKey}"]`);
          row?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        return;
      }
      setFieldErrors(new Set());
    }

    if (!comment.trim()) {
      setError(t('commodities.modifications.commentRequired'));
      commentRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      if (canSelfApprove) {
        // Parse numeric fields before sending
        const parsedChanges: Record<string, any> = {};
        for (const [key, val] of Object.entries(changedFields)) {
          const fieldDef = editableFields.find(f => f.key === key);
          if (fieldDef?.type === 'number') {
            parsedChanges[key] = parseLocaleNumber(String(val));
          } else {
            parsedChanges[key] = val;
          }
        }

        const res = await commodityModificationService.modifyDirect(
          clientId,
          trade.id,
          { modifiedFields: parsedChanges, comment: comment.trim() },
          entityId
        );
        if (res.success) {
          onSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.modifyFailed'));
        }
      } else {
        // Two-step: submit for approval (comment only, no field changes)
        const res = await commodityModificationService.initiate(
          clientId,
          trade.id,
          { action: 'modify', comment: comment.trim() },
          entityId
        );
        if (res.success) {
          onSuccess(res.data);
        } else {
          setError(res.message || t('commodities.modifications.modifyFailed'));
        }
      }
    } catch (err: any) {
      setError(err.message || t('commodities.modifications.modifyFailed'));
    } finally {
      setSubmitting(false);
    }
  }, [hasChanges, comment, clientId, trade.id, changedFields, editableFields, entityId, onSuccess, t, formValues, isOption, canSelfApprove]);

  // Build trade summary line (translated)
  const translatedProduct = t(`commodities.enums.${(trade.ProductType || '').toLowerCase()}`, trade.ProductType || '');
  const translatedDirection = t(`commodities.enums.${(trade.Direction || '').toLowerCase()}`, trade.Direction || '');
  const lotsUnit = t('commodities.modifications.lotsUnit');
  const tradeSummary = `${trade.TradeNumber} — ${translatedProduct} ${translatedDirection} ${trade.UnderlyingName || ''} (${trade.Lots} ${lotsUnit} @ ${formatNumber(trade.Price)} ${trade.UnitOfMeasure || ''})`;

  // Render field input based on type
  const renderFieldInput = (f: FieldConfig) => {
    switch (f.type) {
      case 'select':
        return (
          <select
            value={formValues[f.key] || ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          >
            {f.options?.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'fixingRef':
        return (
          <select
            value={formValues[f.key] || ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          >
            {fixingRefOptions.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        );

      case 'counterparty':
        if (counterpartyOptions.length > 0) {
          return (
            <select
              value={formValues[f.key] || ''}
              onChange={(e) => handleFieldChange(f.key, e.target.value)}
            >
              <option value="">{t('common.select')}</option>
              {counterpartyOptions.map((cp) => (
                <option key={cp.bankId} value={cp.counterpartyName}>{cp.counterpartyName}</option>
              ))}
            </select>
          );
        }
        // Fall back to text input if no counterparties loaded
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          />
        );

      case 'date':
        return (
          <input
            type="date"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          />
        );

      case 'number':
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
            inputMode="decimal"
          />
        );

      default: // 'text'
        return (
          <input
            type="text"
            value={formValues[f.key] ?? ''}
            onChange={(e) => handleFieldChange(f.key, e.target.value)}
          />
        );
    }
  };

  return (
    <div className="commodity-mod-dialog-overlay" onClick={onClose}>
      <div className="commodity-mod-dialog" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="commodity-mod-dialog-header">
          <h2>{canSelfApprove ? t('commodities.modifications.modifyTitle') : t('commodities.modifications.initiateTitle')}</h2>
          <button className="commodity-mod-dialog-close" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="commodity-mod-dialog-body">
          {/* Trade summary */}
          <div className="commodity-mod-trade-summary">
            <div className="trade-title">{tradeSummary}</div>
            <div className="trade-detail">
              {t('commodities.positions.grid.counterpartyName')}: {trade.CounterpartyName || '—'}
            </div>
          </div>

          {/* Non-self-approve: show request explanation instead of fields */}
          {!canSelfApprove && (
            <div className="commodity-mod-warning">
              <AlertTriangle size={16} />
              {t('commodities.modifications.initiateWarning')}
            </div>
          )}

          {/* Self-approve: show editable fields table */}
          {canSelfApprove && (
            <table className="commodity-mod-fields-table" ref={tableRef}>
              <thead>
                <tr>
                  <th>{''}</th>
                  <th>{t('commodities.modifications.currentValue')}</th>
                  <th>{t('commodities.modifications.newValue')}</th>
                </tr>
              </thead>
              <tbody>
                {DIALOG_FIELDS.map((f) => {
                  // Hide option-only fields for non-options
                  if (f.optionOnly && !isOption) return null;

                  const isChanged = f.key in changedFields;

                  // Display-only fields (Product, Underlying, Our Entity)
                  if (f.type === 'display') {
                    return (
                      <tr key={f.key} className="field-display-only">
                        <td className="field-label">{fieldLabel(f.key)}</td>
                        <td className="current-value" colSpan={2}>{trade[f.key] || '—'}</td>
                      </tr>
                    );
                  }

                  // Quantity is read-only — show live-calculated value when Price/Lots change
                  if (f.type === 'readonly') {
                    const showPreview = priceOrLotsChanged && previewQuantity != null;
                    return (
                      <tr key={f.key} className={showPreview ? 'field-changed' : ''}>
                        <td className="field-label">{fieldLabel(f.key)}</td>
                        <td className="current-value">{formatValue(f.key, trade[f.key])}</td>
                        <td className={showPreview ? 'calculated-value' : ''}>
                          {showPreview ? formatNumber(previewQuantity) : formatValue(f.key, trade[f.key])}
                        </td>
                      </tr>
                    );
                  }

                  const hasFieldError = fieldErrors.has(f.key);

                  return (
                    <React.Fragment key={f.key}>
                      <tr data-field={f.key} className={`${isChanged ? 'field-changed' : ''}${hasFieldError ? ' field-error-row' : ''}`}>
                        <td className="field-label">{fieldLabel(f.key)}</td>
                        <td className="current-value">{formatValue(f.key, trade[f.key])}</td>
                        <td className="new-value">
                          {renderFieldInput(f)}
                        </td>
                      </tr>
                      {f.key === 'FixingReference' && maturityMonthChanged && (
                        <tr className="field-hint-row">
                          <td colSpan={3} className="field-hint">
                            <AlertTriangle size={13} />
                            {t('commodities.modifications.maturityMonthWarning')}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Comment */}
          <div className={`commodity-mod-comment${error ? ' has-error' : ''}`} ref={commentRef}>
            <label>{t('commodities.modifications.commentLabel')} *</label>
            <textarea
              value={comment}
              onChange={(e) => { setComment(e.target.value); setError(null); }}
              placeholder={t('commodities.modifications.commentPlaceholder')}
              rows={3}
            />
            {error && <div className="field-error">{error}</div>}
          </div>
        </div>

        {/* Footer */}
        <div className="commodity-mod-dialog-footer">
          <button className="btn-secondary" onClick={onClose} disabled={submitting}>
            {t('commodities.modifications.cancel')}
          </button>
          <button
            className="btn-primary"
            onClick={handleSubmit}
            disabled={submitting || (canSelfApprove && !hasChanges) || (!canSelfApprove && !comment.trim())}
          >
            {submitting
              ? t('commodities.modifications.submitting')
              : canSelfApprove
                ? t('commodities.modifications.confirmModification')
                : t('commodities.modifications.submitRequest')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommodityModifyDialog;
